import { Link, useLocation } from 'react-router-dom';
import { Bell, Menu, MessageSquare } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { brand } from '@/config/brand';
import { useUnreadNotificationCount } from '@api/hooks/useNotifications';
import { useCurrentSubscription } from '@api/hooks/useSubscription';

interface NavbarProps {
  onMenuToggle: () => void;
}

export const Navbar = ({ onMenuToggle }: NavbarProps) => {
  const location = useLocation();
  const { data: subscription, isLoading: subscriptionLoading } = useCurrentSubscription();
  const planFeatures = subscription?.entitlements?.features ?? subscription?.plan?.features ?? {};
  const isVendorTier = planFeatures.vendor_profile === true;
  const { data: unreadCount = 0 } = useUnreadNotificationCount(!subscriptionLoading && !isVendorTier);

  const getPageTitle = (pathname: string): string => {
    const routes: Record<string, string> = {
      '/': '',
      '/upload': '',
      '/reviews': '',
      '/matching': '',
      '/ap-entries': '',
      '/profile': '',
      '/admin/settings': '',
      '/notifications': '',
    };
    return routes[pathname] ?? '';
  };

  return (
    <nav className="bg-surface-base/90 backdrop-blur-sm border-b border-border-default sticky top-0 z-40">
      <div className="px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 hover:bg-surface-raised rounded-lg transition-colors text-content-secondary hover:text-content-primary"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden sm:flex items-center gap-2">
            <img
              src={brand.logoUrl}
              alt={`${brand.name} logo`}
              className="h-8 w-8 object-contain"
            />
            <span className="text-sm font-semibold text-content-secondary">
              {brand.name}
            </span>
          </div>

          <h1 className="text-xl font-bold text-content-primary">
            {getPageTitle(location.pathname)}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden items-center rounded-lg border border-border-default bg-surface-raised p-0.5 text-xs font-medium md:flex">
            <span className="rounded-md bg-surface-base px-2.5 py-1.5 text-primary-700 shadow-sm dark:text-primary-300">Platform</span>
            <Link to="/chat" className="rounded-md px-2.5 py-1.5 text-content-secondary hover:text-content-primary">Chat</Link>
          </div>
          <Link
            to="/chat"
            className="rounded-lg p-2 text-content-secondary transition-colors hover:bg-surface-raised hover:text-content-primary md:hidden"
            aria-label="Switch to chat mode"
          >
            <MessageSquare className="h-5 w-5" />
          </Link>
          {!isVendorTier && (
            <Link
              to="/notifications"
              className="relative rounded-lg p-2 text-content-secondary transition-colors hover:bg-surface-raised hover:text-content-primary"
              aria-label="Open notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-primary-500 px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}
          <UserMenu />
        </div>
      </div>
    </nav>
  );
};
